import { useEffect, useMemo, useState } from 'react'

import { ActionIcon, Checkbox, Group, Skeleton, Text } from '@mantine/core'

import { navigate } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import {
  GET_NOTIFICATIONS,
  MARK_AS_READ,
  MARK_NOTIFICATIONS_AS_READ,
  REPLY_SMS,
} from 'src/graphql/notifications.graphql'
import { UPDATE_CURRENT_EMPLOYEE_ID } from 'src/graphql/users.graphql'
import IconEmptyInbox from 'src/icons/IconEmptyInbox'
import IconLeftArrow from 'src/icons/IconLeftArrow'
import IconRightArrow from 'src/icons/IconRightArrow'
import { useWebsockets } from 'src/pages/ApplicantsPage/context/WebSocketsContext'
import CustomTab from 'src/pages/ActivitiesPage/components/CustomTab'

import { toast } from '../Overlays/Toast/Toast'

import NotificationCard from './NotificationCard'
import { useNotificationFilter } from './NotificationFilterContext'

const NotificationTab = () => {
  const websocketsData = useWebsockets()
  const {
    selectedFilter,
    setTotalCount,
    selectedIds,
    toggleSelectedId,
    selectAllIds,
    clearSelection,
  } = useNotificationFilter()

  const LIMIT = 20
  const [currentPage, setCurrentPage] = useState(0)
  const [includeRead, setIncludeRead] = useState(false)

  // Convert selectedFilter to array format for the query
  const filterTypes = useMemo(
    () => (selectedFilter ? [selectedFilter] : []),
    [selectedFilter]
  )

  // Calculate offset based on current page
  const offset = currentPage * LIMIT

  const { data, refetch, loading } = useQuery(GET_NOTIFICATIONS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      pageInfo: { offset, limit: LIMIT },
      filters: { types: filterTypes, includeRead },
    },
    onError: () => {
      toast('Unable to get notifications, please try again.', 'error')
    },
  })

  const notifications = data?.notifications?.items || []
  const totalCount = data?.notifications?.totalCount || 0
  const totalPages = Math.ceil(totalCount / LIMIT)

  // Update total count in context
  useEffect(() => {
    setTotalCount(totalCount)
  }, [totalCount, setTotalCount])

  // Reset to first page and clear selection when filter changes
  useEffect(() => {
    setCurrentPage(0)
    clearSelection()
  }, [selectedFilter, includeRead, clearSelection])

  const [markAsRead] = useMutation(MARK_AS_READ, {
    onCompleted: () => {
      clearSelection()
      refetch({
        pageInfo: { offset, limit: LIMIT },
        filters: { types: filterTypes, includeRead },
      })
    },
    onError: () => {
      toast('Unable to mark as read, please try again.', 'error')
    },
  })

  const [markNotificationsAsRead, { loading: bulkMarkLoading }] = useMutation(
    MARK_NOTIFICATIONS_AS_READ,
    {
      onCompleted: (data) => {
        const count = data?.markNotificationsAsRead?.count || 0
        toast(
          `Marked ${count} notification${count !== 1 ? 's' : ''} as read`,
          'success'
        )
        clearSelection()
        refetch({
          pageInfo: { offset: 0, limit: LIMIT },
          filters: { types: filterTypes, includeRead },
        })
        setCurrentPage(0)
      },
      onError: () => {
        toast('Unable to mark notifications as read, please try again.', 'error')
      },
    }
  )

  const [replySMS] = useMutation(REPLY_SMS, {
    refetchQueries: [{ query: GET_NOTIFICATIONS }],
    onCompleted: () => {
      toast('Message sent', 'success')
    },
    onError: () => {
      toast('Unable to reply SMS, please try again.', 'error')
    },
  })

  const [updateCurrentEmployeeId] = useMutation(UPDATE_CURRENT_EMPLOYEE_ID, {
    onCompleted: (_data) => {
      toast('Company changed successfully', 'success')
    },
    onError: () => {
      toast('Unable to switch companies. Please try again.', 'error')
    },
  })

  const updateCompanyAndRedirect = async (employeeId, route) => {
    await updateCurrentEmployeeId({
      variables: {
        input: {
          currentEmployeeId: employeeId,
        },
      },
    })
    navigate(route)
    window.location.reload()
  }

  const onReplySMS = ({ notificationId, message }) => {
    replySMS({
      variables: {
        id: notificationId,
        message,
      },
    })
  }

  useEffect(() => {
    if (websocketsData?.newNotification) {
      refetch({
        pageInfo: { offset, limit: LIMIT },
        filters: { types: filterTypes, includeRead },
      })
    }
  }, [websocketsData, refetch, LIMIT, filterTypes, offset, includeRead])

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      clearSelection()
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      clearSelection()
      setCurrentPage(currentPage + 1)
    }
  }

  const handleSelectAll = () => {
    const pageIds = notifications.map((n) => n.notificationId)
    selectAllIds(pageIds)
  }

  const handleMarkSelectedAsRead = () => {
    if (selectedIds.size === 0) return
    markNotificationsAsRead({
      variables: {
        input: {
          notificationIds: Array.from(selectedIds),
        },
      },
    })
  }

  const pageIds = notifications.map((n) => n.notificationId)
  const allOnPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id))

  const NotificationSkeleton = () => {
    return (
      <div className="flex flex-row gap-3 border-b-[1px] border-doubleNickel-gray-200 px-0 py-2">
        {/* Icon skeleton */}
        <div className="flex flex-col items-start gap-1">
          <Skeleton height={16} width={16} />
          <Skeleton height={6} width={6} className="mt-1 rounded-full" />
        </div>
        {/* Content skeleton */}
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex flex-row items-center gap-4">
            <div className="flex flex-1 flex-row items-center gap-1">
              <Skeleton height={14} width={120} />
              <Skeleton height={14} width={80} />
            </div>
            <div className="ml-auto">
              <Skeleton height={10} width={100} />
            </div>
          </div>
          <Skeleton height={12} width="90%" />
          <Skeleton height={12} width="70%" />
        </div>
      </div>
    )
  }

  // Skeletons only on first load; a refetch (e.g. after a task update while
  // the drawer stays open) keeps the cards mounted.
  const isInitialLoading = loading && !data
  const hasScrollableList = isInitialLoading || notifications.length > 0

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Toolbar: read/unread toggle always visible; bulk actions when items exist */}
      <div className="z-10 flex shrink-0 flex-row items-center justify-between border-b border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-0 py-1.5">
        <div className="flex flex-row items-center gap-4">
          {notifications.length > 0 && (
            <div className="flex flex-row items-center gap-2">
              <Checkbox
                size={14}
                checked={allOnPageSelected}
                indeterminate={selectedIds.size > 0 && !allOnPageSelected}
                onChange={handleSelectAll}
                classNames={{
                  input:
                    'checked:bg-doubleNickel-brand-500 checked:border-doubleNickel-brand-500',
                }}
              />
              <Text size="xs" className="text-doubleNickel-gray-600">
                Select all ({notifications.length})
              </Text>
            </div>
          )}
          {notifications.length > 0 && selectedIds.size > 0 && (
            <div className="flex flex-row items-center gap-2">
              <Text size="xs" className="text-doubleNickel-gray-600">
                {selectedIds.size} selected
              </Text>
              <span className="text-xs text-doubleNickel-gray-400">
                &middot;
              </span>
              <button
                onClick={handleMarkSelectedAsRead}
                disabled={bulkMarkLoading}
                className="text-xs font-medium text-doubleNickel-brand-500 transition-colors hover:text-doubleNickel-brand-600 disabled:text-doubleNickel-gray-400"
              >
                {bulkMarkLoading ? 'Marking...' : 'Mark as read'}
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-row items-center gap-0.5 rounded-lg bg-doubleNickel-gray-100 p-0.5">
          <CustomTab
            selectedTab={includeRead ? 'all' : 'unread'}
            value="unread"
            label="Unread"
            setSelectedTab={() => setIncludeRead(false)}
            variant="segmented"
          />
          <CustomTab
            selectedTab={includeRead ? 'all' : 'unread'}
            value="all"
            label="All"
            setSelectedTab={() => setIncludeRead(true)}
            variant="segmented"
          />
        </div>
      </div>
      <div
        className={`min-h-0 flex-1 ${
          hasScrollableList
            ? 'overflow-y-auto'
            : 'flex flex-col overflow-hidden'
        }`}
      >
        {isInitialLoading &&
          Array(5)
            .fill(0)
            .map((_, index) => <NotificationSkeleton key={index} />)}
        {!isInitialLoading && notifications.length === 0 && (
          <div className="flex flex-1 flex-col items-center justify-center">
            <IconEmptyInbox className="fill-none" />
            <div className="text-md font-medium text-doubleNickel-gray-600">
              {includeRead
                ? 'You have no notifications'
                : 'You have no new notifications'}
            </div>
          </div>
        )}
        {!isInitialLoading &&
          notifications.map((notification) => (
            <NotificationCard
              key={notification.notificationId}
              notification={notification}
              updateCompanyAndRedirect={updateCompanyAndRedirect}
              markAsRead={markAsRead}
              onReplySMS={onReplySMS}
              isSelected={selectedIds.has(notification.notificationId)}
              onToggleSelect={() =>
                toggleSelectedId(notification.notificationId)
              }
            />
          ))}
      </div>
      {!isInitialLoading && (
        <div className="shrink-0 flex flex-col border-t border-doubleNickel-gray-200">
          {notifications.length > 0 && totalPages > 1 && (
            <div className="flex flex-row items-center justify-between px-4 py-1">
              <Text size="xs" className="text-doubleNickel-gray-600">
                Page {currentPage + 1} of {totalPages} ({totalCount} total)
              </Text>
              <Group gap="xs">
                <ActionIcon
                  variant="subtle"
                  size="xs"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 0 || loading}
                  className="hover:bg-doubleNickel-gray-100"
                >
                  <IconLeftArrow className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-600" />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  size="xs"
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages - 1 || loading}
                  className="hover:bg-doubleNickel-gray-100"
                >
                  <IconRightArrow className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-600" />
                </ActionIcon>
              </Group>
            </div>
          )}
          <Text
            size="xs"
            className={`px-4 py-1 text-doubleNickel-gray-500${
              notifications.length > 0 && totalPages > 1
                ? ' border-t border-doubleNickel-gray-200'
                : ''
            }`}
          >
            Notifications older than 30 days are not shown
          </Text>
        </div>
      )}
    </div>
  )
}

export default NotificationTab
